/**
 * Nostr platform adapter.
 * Maps Nostr relay events to the normalized Post type used by all UI components.
 */
import { nip19 } from 'nostr-tools'
import { isValidRelayUrl } from '@/lib/nostr'
import type { NostrSession } from '@/types/nostr'
import type {
    PlatformAdapter,
    PlatformCapabilities,
    AdapterSession,
    TimelineOptions,
    SearchResult,
    FollowStatus,
} from './types'
import type { Post, PostsPage, PostAuthor, PostMetrics, PostEmbed } from '@/types/posts'
import type { NotificationsPage } from '@/types/notifications'
import {
    fetchNostrTimeline,
    likeNostrPost,
    repostNostrPost,
    publishDeleteEvent,
    fetchContactList,
    updateContactList,
    fetchNostrAuthorPosts,
    fetchNostrHashtagPosts,
    fetchNostrProfile,
    fetchNostrProfiles,
    fetchEngagementCounts,
    type NostrEvent,
    type NostrProfileEvent,
    type EngagementCounts,
} from '@/services/nostrService'

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|avif)(\?[^\s]*)?$/i
const VIDEO_EXTENSIONS = /\.(mp4|webm|mov)(\?[^\s]*)?$/i
const IMAGE_MIME_SUBTYPES = new Set(['jpeg', 'jpg', 'png', 'gif', 'webp', 'avif'])
const VIDEO_MIME_SUBTYPES = new Set(['mp4', 'webm', 'mov'])

function extractNostrEmbeds(event: NostrEvent): { embed?: PostEmbed; text: string } {
    const images: NonNullable<PostEmbed['images']> = []
    let videoEmbed: NonNullable<PostEmbed['video']> | undefined
    const imetaTags = event.tags.filter(t => t[0] === 'imeta')

    for (const tag of imetaTags) {
        const pairs: Record<string, string> = {}
        for (let i = 1; i < tag.length; i++) {
            const spaceIdx = tag[i].indexOf(' ')
            if (spaceIdx > 0) {
                pairs[tag[i].slice(0, spaceIdx)] = tag[i].slice(spaceIdx + 1)
            }
        }
        const url = pairs['url']
        if (!url || !url.startsWith('https://')) continue
        const mime = pairs['m']?.toLowerCase() ?? ''
        const isFullVideoMime = mime.startsWith('video/')
        const isBareVideoSubtype = VIDEO_MIME_SUBTYPES.has(mime)
        const isFullImageMime = mime.startsWith('image/')
        const isBareImageSubtype = IMAGE_MIME_SUBTYPES.has(mime)

        const isVideo =
            isFullVideoMime || isBareVideoSubtype || (!mime && VIDEO_EXTENSIONS.test(url))
        const isImage =
            isFullImageMime || isBareImageSubtype || (!mime && IMAGE_EXTENSIONS.test(url))

        if (isVideo) {
            let aspectRatio: { width: number; height: number } | undefined
            const dimMatch = pairs['dim']?.match(/^([\d.]+)x([\d.]+)$/)
            if (dimMatch) {
                aspectRatio = {
                    width: Math.round(parseFloat(dimMatch[1])),
                    height: Math.round(parseFloat(dimMatch[2])),
                }
            }
            videoEmbed = {
                playlist: url,
                aspectRatio,
                alt: pairs['alt'],
                thumbnail: pairs['thumb']?.startsWith('https://') ? pairs['thumb'] : undefined,
            }
            continue
        }
        if (!isImage) continue
        let aspectRatio: { width: number; height: number } | undefined
        const dimMatch = pairs['dim']?.match(/^([\d.]+)x([\d.]+)$/)
        if (dimMatch) {
            aspectRatio = {
                width: Math.round(parseFloat(dimMatch[1])),
                height: Math.round(parseFloat(dimMatch[2])),
            }
        }
        images.push({ url, alt: pairs['alt'], aspectRatio })
    }

    let text = event.content

    if (imetaTags.length === 0) {
        const urlMatches = text.match(/https:\/\/[^\s<>"]+/gi) ?? []
        const IMAGE_EXTENSIONS_SET = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif'])
        const VIDEO_EXTENSIONS_SET = new Set(['mp4', 'webm', 'mov'])

        for (const rawUrl of urlMatches) {
            const url = rawUrl.replace(/[.,!?;:)>]+$/, '')
            if (!url.startsWith('https://')) continue // SSRF guard
            try {
                const parsed = new URL(url)
                const pathname = parsed.pathname.toLowerCase()
                const ext = pathname.split('.').pop() ?? ''
                if (IMAGE_EXTENSIONS_SET.has(ext)) {
                    images.push({ url })
                    text = text.split(rawUrl).join('')
                } else if (VIDEO_EXTENSIONS_SET.has(ext)) {
                    if (!videoEmbed) {
                        videoEmbed = { playlist: url }
                        text = text.split(rawUrl).join('')
                    }
                }
            } catch {
                // Invalid URL, skip
            }
        }
    }

    if (!videoEmbed && images.length === 0) {
        return { text: event.content }
    }

    if (videoEmbed) {
        text = text.split(videoEmbed.playlist).join('')
    }
    if (videoEmbed?.thumbnail) {
        text = text.split(videoEmbed.thumbnail).join('')
    }
    for (const img of images) {
        text = text.split(img.url).join('')
    }
    text = text
        .replace(/\n{3,}/g, '\n\n')
        .replace(/ {2,}/g, ' ')
        .trim()

    if (videoEmbed) {
        return { embed: { type: 'video', video: videoEmbed }, text }
    }
    return { embed: { type: 'images', images }, text }
}

function toPostAuthor(
    pubkey: string,
    profile?: { name?: string; display_name?: string; picture?: string; nip05?: string } | null,
    followedPubkeys?: Set<string>
): PostAuthor {
    const npub = nip19.npubEncode(pubkey)
    const shortNpub = `${npub.slice(0, 8)}...${npub.slice(-4)}`
    return {
        did: pubkey,
        handle: npub,
        // An empty name string is meaningfully "no display handle", same as missing.
        displayHandle: profile?.nip05
            ? profile.nip05.startsWith('_@')
                ? profile.nip05.slice(2)
                : profile.nip05
            : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              profile?.name || undefined,
        // Fallback chain: an empty display_name/name should fall through to the next candidate.
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        displayName: profile?.display_name || profile?.name || shortNpub,
        avatarUrl: profile?.picture,
        protocol: 'nostr',
        followingUri: followedPubkeys?.has(pubkey) ? pubkey : undefined,
    } as unknown as PostAuthor
}

export function nostrEventToPost(
    event: NostrEvent,
    profileMap?: Map<string, NostrProfileEvent>,
    engagementMap?: Map<string, EngagementCounts>,
    followedPubkeys?: Set<string>
): Post {
    const author = toPostAuthor(event.pubkey, profileMap?.get(event.pubkey), followedPubkeys)
    const engagement = engagementMap?.get(event.id)
    const metrics: PostMetrics = {
        likeCount: engagement?.likeCount ?? 0,
        repostCount: engagement?.repostCount ?? 0,
        replyCount: engagement?.replyCount ?? 0,
    }

    // NIP-10 reply detection: reply marker > root marker > last non-mention e-tag (positional)
    const eTags = event.tags.filter(t => t[0] === 'e')
    const nonMentionTags = eTags.filter(t => t[3] !== 'mention')
    const isReply = nonMentionTags.length > 0
    const explicitReply = nonMentionTags.find(t => t[3] === 'reply')
    const rootTag = nonMentionTags.find(t => t[3] === 'root')
    const parentTag = explicitReply ?? nonMentionTags[nonMentionTags.length - 1]
    const replyParentUri = isReply ? `nostr:${parentTag[1]}` : undefined
    const replyParentRelayHint =
        isReply && parentTag[2] && isValidRelayUrl(parentTag[2]) ? parentTag[2] : undefined
    const replyRootUri = isReply ? `nostr:${rootTag?.[1] ?? nonMentionTags[0][1]}` : undefined

    const { embed, text } = extractNostrEmbeds(event)

    return {
        uri: `nostr:${event.id}`,
        cid: undefined,
        text,
        embed,
        createdAt: new Date(event.created_at * 1000).toISOString(),
        isReply,
        replyParentUri,
        replyParentRelayHint,
        replyRootUri,
        author,
        metrics,
        likeUri: engagement
            ? engagement.likeUri
                ? `nostr:${engagement.likeUri}`
                : null
            : undefined,
        repostUri: engagement
            ? engagement.repostUri
                ? `nostr:${engagement.repostUri}`
                : null
            : null,
        isActivityPub: false,
        protocol: 'nostr',
    }
}

function sessionAs(session: AdapterSession): NostrSession {
    if (!session || typeof (session as NostrSession).pubkey !== 'string') {
        throw new Error('[nostrAdapter] invalid session: expected NostrSession with pubkey')
    }
    return session as NostrSession
}

export const nostrAdapter: PlatformAdapter = {
    platform: 'nostr',

    capabilities: {
        reply: true,
        like: true,
        repost: true,
        quote: false,
        delete: true,
        follow: true,
        feeds: false,
        hashtags: true,
        gridView: false,
        likedPostsFeed: false,
        favouritesFeed: false,
        profileRepliesTab: false,
        bookmarks: false,
        // Nostr has no pinned-post concept
        pinPosts: false,
        lists: false,
    } satisfies PlatformCapabilities,

    async fetchTimeline(session: AdapterSession, options?: TimelineOptions): Promise<PostsPage> {
        const nostrSession = sessionAs(session)
        // Convert cursor (unix timestamp string) to `until` param
        const parsedTimeline = options?.cursor ? parseInt(options.cursor, 10) : undefined
        const until =
            parsedTimeline !== undefined && Number.isFinite(parsedTimeline) && parsedTimeline > 0
                ? parsedTimeline
                : undefined
        const [events, follows] = await Promise.all([
            fetchNostrTimeline(nostrSession, { limit: 50, until }),
            fetchContactList(nostrSession),
        ])
        const followedPubkeys = new Set([nostrSession.pubkey, ...follows])
        const uniquePubkeys = [...new Set(events.map(e => e.pubkey))]
        const eventIds = events.map(e => e.id)
        const [profileMap, engagementMap] = await Promise.all([
            fetchNostrProfiles(uniquePubkeys, nostrSession.relays),
            fetchEngagementCounts(eventIds, {
                userPubkey: nostrSession.pubkey,
                relays: nostrSession.relays,
            }),
        ])
        const posts = events.map(e =>
            nostrEventToPost(e, profileMap, engagementMap, followedPubkeys)
        )
        // Cursor is the oldest event's created_at for pagination
        const cursor =
            events.length > 0 ? String(events[events.length - 1].created_at - 1) : undefined
        return { posts, cursor }
    },

    async likePost(
        session: AdapterSession,
        post: Post
    ): Promise<{ uri: string | null; liked: boolean }> {
        const nostrSession = sessionAs(session)
        const eventId = post.uri.replace('nostr:', '')
        // We need the author pubkey from the post
        const fakeEvent: NostrEvent = {
            id: eventId,
            pubkey: post.author?.did ?? '',
            created_at: 0,
            kind: 1,
            tags: [],
            content: '',
            sig: '',
        }
        const reactionId = await likeNostrPost(nostrSession, fakeEvent)
        return { uri: `nostr:${reactionId}`, liked: true }
    },

    async unlikePost(session: AdapterSession, post: Post): Promise<void> {
        // Nostr has no unlike — publish a delete event for the reaction
        const nostrSession = sessionAs(session)
        if (post.likeUri) {
            const reactionId = post.likeUri.replace('nostr:', '')
            await publishDeleteEvent(nostrSession, reactionId)
        }
    },

    async repostPost(
        session: AdapterSession,
        post: Post
    ): Promise<{ uri: string | null; reposted: boolean }> {
        const nostrSession = sessionAs(session)
        const eventId = post.uri.replace('nostr:', '')
        const fakeEvent: NostrEvent = {
            id: eventId,
            pubkey: post.author?.did ?? '',
            created_at: Math.floor(new Date(post.createdAt).getTime() / 1000),
            kind: 1,
            tags: [],
            content: post.text,
            sig: '',
        }
        const repostId = await repostNostrPost(nostrSession, fakeEvent)
        return { uri: `nostr:${repostId}`, reposted: true }
    },

    async unrepostPost(session: AdapterSession, post: Post): Promise<void> {
        const nostrSession = sessionAs(session)
        if (post.repostUri) {
            const repostId = post.repostUri.replace('nostr:', '')
            await publishDeleteEvent(nostrSession, repostId)
        }
    },

    async deletePost(session: AdapterSession, post: Post): Promise<void> {
        const nostrSession = sessionAs(session)
        const eventId = post.uri.replace('nostr:', '')
        await publishDeleteEvent(nostrSession, eventId)
    },

    searchUsers(_session: AdapterSession, _query: string): Promise<SearchResult[]> {
        // Nostr user search not supported in Phase 1
        return Promise.resolve([])
    },

    searchPosts(_session: AdapterSession, _query: string): Promise<PostsPage> {
        return Promise.resolve({ posts: [] })
    },

    fetchNotifications(
        _session: AdapterSession,
        _options?: { limit?: number; cursor?: string }
    ): Promise<NotificationsPage> {
        return Promise.resolve({ notifications: [] })
    },

    getUnreadCount(_session: AdapterSession): Promise<number> {
        return Promise.resolve(0)
    },

    async fetchAuthorPosts(
        session: AdapterSession,
        identifier: string,
        options?: { cursor?: string; filter?: string }
    ): Promise<PostsPage> {
        // identifier is a hex pubkey or npub
        let pubkey = identifier
        if (identifier.startsWith('npub')) {
            try {
                const decoded = nip19.decode(identifier)
                if (decoded.type === 'npub') {
                    pubkey = decoded.data
                }
            } catch {
                // Invalid npub — fall through with raw value
            }
        }

        const parsedAuthor = options?.cursor ? parseInt(options.cursor, 10) : undefined
        const until =
            parsedAuthor !== undefined && Number.isFinite(parsedAuthor) && parsedAuthor > 0
                ? parsedAuthor
                : undefined
        const events = await fetchNostrAuthorPosts(pubkey, { limit: 50, until })
        const nostrSession =
            typeof (session as NostrSession)?.pubkey === 'string' ? (session as NostrSession) : null
        const [profileMap, engagementMap] = await Promise.all([
            fetchNostrProfiles([pubkey]),
            fetchEngagementCounts(
                events.map(e => e.id),
                {
                    userPubkey: nostrSession?.pubkey,
                    relays: nostrSession?.relays,
                }
            ),
        ])
        const posts = events.map(e => nostrEventToPost(e, profileMap, engagementMap))
        const cursor =
            events.length > 0 ? String(events[events.length - 1].created_at - 1) : undefined
        return { posts, cursor }
    },

    async getFollowStatus(session: AdapterSession, identifier: string): Promise<FollowStatus> {
        const nostrSession = sessionAs(session)
        let pubkey = identifier
        if (identifier.startsWith('npub')) {
            try {
                const decoded = nip19.decode(identifier)
                if (decoded.type === 'npub') pubkey = decoded.data
            } catch {
                // fall through
            }
        }
        const follows = await fetchContactList(nostrSession)
        const isFollowing = follows.includes(pubkey)
        return { isFollowing, followRef: pubkey }
    },

    async follow(session: AdapterSession, identifier: string): Promise<FollowStatus> {
        const nostrSession = sessionAs(session)
        let pubkey = identifier
        if (identifier.startsWith('npub')) {
            try {
                const decoded = nip19.decode(identifier)
                if (decoded.type === 'npub') pubkey = decoded.data
            } catch {
                // fall through
            }
        }
        const current = await fetchContactList(nostrSession)
        if (!current.includes(pubkey)) {
            await updateContactList(nostrSession, [...current, pubkey])
        }
        return { isFollowing: true, followRef: pubkey }
    },

    async unfollow(session: AdapterSession, identifier: string): Promise<void> {
        const nostrSession = sessionAs(session)
        let pubkey = identifier
        if (identifier.startsWith('npub')) {
            try {
                const decoded = nip19.decode(identifier)
                if (decoded.type === 'npub') pubkey = decoded.data
            } catch {
                // fall through
            }
        }
        const current = await fetchContactList(nostrSession)
        await updateContactList(
            nostrSession,
            current.filter(pk => pk !== pubkey)
        )
    },

    async fetchHashtagPosts(
        session: AdapterSession,
        tag: string,
        cursor?: string
    ): Promise<PostsPage> {
        const parsedHashtag = cursor ? parseInt(cursor, 10) : undefined
        const until =
            parsedHashtag !== undefined && Number.isFinite(parsedHashtag) && parsedHashtag > 0
                ? parsedHashtag
                : undefined
        const nostrSession =
            typeof (session as NostrSession)?.pubkey === 'string' ? (session as NostrSession) : null
        const events = await fetchNostrHashtagPosts(tag, { limit: 50, until }, nostrSession?.relays)
        const engagementMap = await fetchEngagementCounts(
            events.map(e => e.id),
            {
                userPubkey: nostrSession?.pubkey,
                relays: nostrSession?.relays,
            }
        )
        const posts = events.map(e => nostrEventToPost(e, undefined, engagementMap))
        const lastEvent = events[events.length - 1]
        const nextCursor = lastEvent ? String(lastEvent.created_at - 1) : undefined
        return { posts, cursor: nextCursor }
    },
}

// Re-export fetchNostrProfile so auth/profile code can use it
export { fetchNostrProfile }
